import { getColor, mode, StyleFunctionProps } from '@chakra-ui/theme-tools';
import { sizes as commonSizes } from './common';

const baseStyle = {
  field: {
    width: '100%',
    outline: 0,
    position: 'relative',
    appearance: 'none',
    transition: 'all 0.2s',
    bg: 'inherit',
  },
};

function getDefaults(props: StyleFunctionProps) {
  const { focusBorderColor: fc, errorBorderColor: ec, colorScheme: cs = 'gray' } = props;
  return {
    focusBorderColor: fc || mode(cs === 'gray' ? `${cs}.900` : `${cs}.400`, `${cs}.300`)(props),
    errorBorderColor: ec || mode('red.800', 'red.700')(props),
  };
}

function variantOutline(props: StyleFunctionProps) {
  const { theme, colorScheme: cs = 'gray' } = props;
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);
  return {
    field: {
      border: '2px solid',
      borderColor: mode(`${cs}.200`, 'whiteAlpha.300')(props),
      bg: mode('transparent', 'whiteAlpha.200')(props),
      _hover: {
        borderColor: mode(`${cs}.300`, 'whiteAlpha.400')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        bg: mode('gray.100', 'whiteAlpha.100')(props),
        userSelect: 'all',
        _focus: {
          borderColor: mode(`${cs}.200`, 'whiteAlpha.400')(props),
        },
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _focus: {
        zIndex: 1,
        borderColor: getColor(theme, fc),
        boxShadow: 'none',
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: 'none',
      },
    },
    addon: {
      border: '2px solid',
      borderColor: mode(`${cs}.300`, `${cs}.400`)(props),
      bg: 'transparent',
    },
  };
}

function variantFeedItem(props: StyleFunctionProps) {
  const { theme, colorScheme: cs = 'gray' } = props;
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);
  return {
    field: {
      border: '2px solid',
      borderColor: mode(`${cs}.700`, 'whiteAlpha.300')(props),
      bg: mode('transparent.gray-900-50', 'whiteAlpha.200')(props),
      px: 4,
      py: 3,
      rounded: 'md',
      _hover: {
        borderColor: mode(`${cs}.700`, 'whiteAlpha.400')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        bg: mode('gray.100', 'whiteAlpha.100')(props),
        userSelect: 'all',
        _focus: {
          borderColor: mode(`${cs}.200`, 'whiteAlpha.400')(props),
        },
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _focus: {
        zIndex: 1,
        borderColor: getColor(theme, fc),
        boxShadow: 'none',
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: 'none',
      },
    },
    addon: {
      border: '2px solid',
      borderColor: mode(`${cs}.300`, `${cs}.400`)(props),
      bg: 'transparent',
    },
  };
}

export const size = {
  xl: {
    ...commonSizes.xl,
    px: 6,
    borderRadius: 'xl',
  },
  lg: {
    ...commonSizes.lg,
    px: 6,
    borderRadius: 'xl',
  },
  md: {
    ...commonSizes.md,
    px: 5,
    borderRadius: 'lg',
  },
  def: {
    ...commonSizes.def,
    px: 5,
    borderRadius: 'lg',
  },
  sm: {
    ...commonSizes.sm,
    px: 4,
    borderRadius: 'lg',
  },
  xs: {
    ...commonSizes.xs,
    px: 3,
    borderRadius: 'md',
  },
  '2xs': {
    ...commonSizes['2xs'],
    px: 2,
    borderRadius: 'md',
  },
};

export const sizes = {
  xl: {
    field: size.xl,
    addon: size.xl,
  },
  lg: {
    field: size.lg,
    addon: size.lg,
  },
  md: {
    field: size.md,
    addon: size.md,
  },
  def: {
    field: size.def,
    addon: size.def,
  },
  sm: {
    field: size.sm,
    addon: size.sm,
  },
  xs: {
    field: size.xs,
    addon: size.xs,
  },
  '2xs': {
    field: size['2xs'],
    addon: size['2xs'],
  },
};

const variants = {
  outline: variantOutline,
  feedItem: variantFeedItem,
};

const defaultProps = {
  size: 'md',
  variant: 'outline',
};

export default {
  baseStyle,
  sizes,
  variants,
  defaultProps,
};
